import { Button } from "@/src/components/buttons";
import SearchBar from "@/src/components/searchBar";
import { uid } from "@/src/utils/utils";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { IoRefresh } from "react-icons/io5";
import { useInView } from "react-intersection-observer";
import { AiDescription } from "../app/aiTasks/aiDescription";

export function FetchableList<T>({
  queryFn,
  queryKey,
  searchKey,
  shouldFocus,
  onSearch,
  onClick,
  emptyChild,
  children,
}: {
  queryFn:
    | Promise<T[] | null | undefined>
    | (() => Promise<T[] | null | undefined>)
    | (() => T[] | null | undefined);
  queryKey: any[];
  searchKey?: string | null | undefined;
  shouldFocus?: boolean;
  onSearch?: (newValue: string | null) => void;
  onClick?: (e: any) => void;
  emptyChild?: any;
  children: (data: T[] | null | undefined) => any;
}) {
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKey,
    queryFn: () => (typeof queryFn == "function" ? queryFn?.() : queryFn),
  });

  return (
    <ListConstructor
      data={data}
      error={error}
      isError={isError}
      isLoading={isLoading}
      emptyChild={emptyChild}
      searchKey={searchKey}
      onSearch={onSearch}
      onClick={onClick}
      shouldFocus={shouldFocus}
    >
      {children(data || [])}
    </ListConstructor>
  );
}

export function InfiniteFetchableList<T>({
  getFunction,
  filtersComponent,
  queryKey,
  limit,
  showRefreshButton,
  onRefreshClick,
  searchKey,
  buttonTitle,
  buttonPosition = "right",
  onSearch,
  shouldFocus,
  emptyChild,
  children,
  onClick,
}: {
  getFunction: (
    limit: number,
    offset: number,
    searchKey: string | null | undefined
  ) => Promise<T[] | null | undefined> | null;

  filtersComponent?: any;
  queryKey: any[];
  limit?: number;
  searchKey?: string | null | undefined;
  showRefreshButton?: boolean;
  onRefreshClick?: () => void;
  onSearch?: (newValue: string | null) => void;
  buttonPosition?: "right" | "left" | "bottom";
  buttonTitle?: string;
  shouldFocus?: boolean;
  emptyChild?: any;
  onClick?: (e: any) => void;
  children: (data: T[] | null | undefined) => any;
}) {
  const [uniqueId, setUniqueId] = useState(uid());
  limit ||= 10;

  let {
    data,
    status,
    isLoading,
    error,
    isError,
    fetchNextPage,

    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [...queryKey, searchKey],
    queryFn: ({ direction, meta, pageParam, queryKey, signal }) => {
      let offset = pageParam * (limit || 10);

      return getFunction(limit!, offset, searchKey);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (allPages?.length > 0) {
        let hasNext = true;
        for (let page of allPages || []) {
          if ((page?.length || 0) >= limit!) continue;
          hasNext = false;
          break;
        }
        if (!hasNext) return undefined;
      }
      return lastPageParam + 1;
    },
  });

  let isEmpty = true;
  for (let page of data?.pages || []) {
    if (!page) continue;
    if (page?.length > 0) {
      isEmpty = false;
      break;
    }
  }

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage]);

  return (
    <div className="infiniteList relative">
      <div className="c1 gap10">
        {onSearch && (
          <div
            className={`${buttonPosition == "bottom" ? "c1" : "c1-auto"} gap10`}
          >
            <SearchBar
              placeholder="Rechercher..."
              value={searchKey}
              onChange={onSearch}
              autoFocus={shouldFocus}
              onEnter={(e, newValue) => {
                onSearch?.(newValue);
                setTimeout(() => {
                  onClick?.(e);
                }, 100);
              }}
            ></SearchBar>

            <div className={`c1-auto gap10 acenter`}>
              {filtersComponent != null && filtersComponent}
              {onClick && (
                <Button
                  additionalStyle={{ height: "100%" }}
                  onClick={onClick}
                  description={`Add a new item of type ${queryKey[0] || ""} `}
                >
                  {buttonTitle || "Ajouter"}
                </Button>
              )}
            </div>
          </div>
        )}

        {isEmpty &&
          !isLoading &&
          !isError &&
          (emptyChild != null ? (
            <AiDescription
              item={{
                title: "No item found",
                type: "simpleText",
                content: emptyChild,
              }}
            >
              {emptyChild}
            </AiDescription>
          ) : (
            <div className="grid c1 gap10 center p30 gray">
              <div>Aucun élément</div>
              {onClick != null && (
                <Button type="tertiary" onClick={onClick}>
                  {buttonTitle || "Créer un élément"}
                </Button>
              )}
            </div>
          ))}

        {!isEmpty && !isLoading && !isError
          ? data?.pages.map((page, index) => {
              return (
                <div key={`${uniqueId}-${index}`}>{children(page || [])}</div>
              );
            })
          : null}

        {isError && (
          <div className="red-bg lc p20 center">
            Error {JSON.stringify(error?.message) || ""}
          </div>
        )}
        {(isLoading || status == "pending" || isFetchingNextPage == true) && (
          <div className=" gray grid c1 gap10">
            <div className="skeletonTile"></div>
            <div className="skeletonTile"></div>
            <div className="skeletonTile"></div>
          </div>
        )}
      </div>

      <div ref={ref} className="mtop10">
        <div
          style={{ height: "10px", width: "100%", background: "transparent" }}
        ></div>
      </div>
      {showRefreshButton && (
        <div
          style={{
            bottom: 20,
            left: 0,
            right: 0,
            position: "fixed",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            color="green"
            icon={<IoRefresh size={20} />}
            onClick={() => onRefreshClick?.()}
          >
            <strong>Rafraîchir</strong> <br /> (Nouveaux éléments disponibles)
          </Button>
        </div>
      )}
    </div>
  );

  // return (

  // );
}

export function ListConstructor({
  data,
  isLoading,
  isError,
  error,
  emptyChild,
  searchKey,
  filtersComponent,
  shouldFocus,
  onSearch,
  onClick,
  children,
}: {
  data: any[] | null | undefined;
  isLoading: boolean;
  isError?: boolean;
  error?: any;
  emptyChild?: any;
  filtersComponent?: any;
  searchKey?: string | null | undefined;
  onSearch?: (newValue: string) => void;
  shouldFocus?: boolean;
  onClick?: (e: any) => void;
  children: any;
}) {
  let isEmpty = data && !data?.length && emptyChild !== false;
  return (
    <div className="c1 gap10">
      {onSearch && (
        <div className="c1-auto gap10">
          <SearchBar
            placeholder="Rechercher..."
            value={searchKey}
            onChange={onSearch}
            autoFocus={shouldFocus}
            onEnter={(e, newValue) => {
              onSearch?.(newValue);
              setTimeout(() => {
                onClick?.(e);
              }, 100);
            }}
          ></SearchBar>
          <div className="c1-auto gap10 acenter">
            {filtersComponent != null && filtersComponent}
            {onClick && <Button onClick={onClick}>Ajouter</Button>}
          </div>
        </div>
      )}
      {isLoading && (
        <div className=" gray grid c1 gap10">
          <div className="skeletonTile"></div>
          <div className="skeletonTile"></div>
          <div className="skeletonTile"></div>
        </div>
      )}
      {isError && (
        <div className="red-bg lc p20 center">
          Error {JSON.stringify(error.message) || ""}
        </div>
      )}
      {isEmpty &&
        !isLoading &&
        !isError &&
        (emptyChild != null ? (
          <AiDescription
            item={{
              title: "No item found",
              type: "simpleText",
              content: emptyChild,
            }}
          >
            {emptyChild}
          </AiDescription>
        ) : (
          <div className="c1 center gap20 p30 gray">
            <div>Aucun élément trouvé</div>
            {onClick != null && (
              <Button type="tertiary" onClick={onClick}>
                Ajouter
              </Button>
            )}
          </div>
        ))}
      {!isEmpty && !isLoading && !isError && children}
    </div>
  );
}

export function StreamListConstructor({
  data,
  isLoading,
  isError,
  error,
  emptyChild,
  searchKey,
  onSearch,
  onClick,
  children,
}: {
  data: any[] | null | undefined;
  isLoading: boolean;
  isError?: boolean;
  error?: any;
  emptyChild?: any;
  searchKey?: string | null | undefined;
  onSearch?: (newValue: string) => void;
  onClick?: (e: any) => void;
  children: any;
}) {
  let isEmpty = data && !data?.length && emptyChild !== false;
  return (
    <div className="c1 gap10">
      {onSearch && (
        <div className="c1-auto gap10">
          <SearchBar
            placeholder="Rechercher..."
            value={searchKey}
            onChange={onSearch}
          ></SearchBar>
          {onClick && <Button onClick={onClick}>Ajouter</Button>}
        </div>
      )}
      {isLoading && (
        <div className=" gray grid c1 gap10">
          <div className="skeletonTile"></div>
          <div className="skeletonTile"></div>
          <div className="skeletonTile"></div>
        </div>
      )}
      {isError && (
        <div className="red-bg lc p20 center">
          Error {JSON.stringify(error.message) || ""}
        </div>
      )}
      {isEmpty &&
        !isLoading &&
        !isError &&
        (emptyChild || (
          <div className="c1 center gap20 p30 gray">
            <div>Aucun élément trouvé</div>
            {onClick != null && (
              <Button type="tertiary" onClick={onClick}>
                Ajouter
              </Button>
            )}
          </div>
        ))}
      {!isEmpty && !isLoading && !isError && children}
    </div>
  );
}
